import { Sprite, Assets } from 'pixi.js';
import gsap from 'gsap';
import { BaseScene } from './BaseScene';
import { UIButton } from '../components/UIButton';
import { WarriorSelectionPanel } from '../components/WarriorSelectionPanel';
import { WaitingOverlay } from '../components/WaitingOverlay';
import { FONTS, SPACING } from '../constants/theme';
import { QRService } from '@services/QRService';
import { SoundManager } from '@services/SoundManager';
import { QRCanvas, frontalCamera, frameLoop } from 'qr/dom.js';
import type { LayoutMetrics } from '../LayoutManager';
import type { GameState } from '@/types';

export interface WarriorSelectionSceneOptions {
  getState: () => GameState;
  getPlayerNumber: () => 1 | 2;
  getPlayerColor: () => number;
  getDefaultName: () => string;
  getDefaultRating: () => number;
  onNext: (playerName: string, selections: string[], rating: number) => void;
  onBack: () => void;
}

const WARRIOR_CARD_ASPECT_RATIO = 0.7; // width / height - matches game board cards

/**
 * Unified warrior selection scene for both Player 1 and Player 2
 */
export class WarriorSelectionScene extends BaseScene {
  private metrics: LayoutMetrics;
  private options: WarriorSelectionSceneOptions;

  // Background and title
  private backgroundSprite: Sprite | null = null;
  private titleSprite: Sprite | null = null;

  // Warrior selection panel (shared component)
  private selectionPanel: WarriorSelectionPanel | null = null;

  // Navigation buttons
  private backButton: UIButton | null = null;
  private scanQRButton: UIButton | null = null;
  private nextButton: UIButton | null = null;

  // QR Scanner overlay
  private scannerOverlay: HTMLDivElement | null = null;
  private scannerVideo: HTMLVideoElement | null = null;
  private qrCanvas: QRCanvas | null = null;
  private cancelFrameLoop: (() => void) | null = null;
  private activeCamera: { stop: () => void } | null = null;

  // Waiting overlay for online mode
  private waitingOverlay: WaitingOverlay | null = null;

  constructor(metrics: LayoutMetrics, options: WarriorSelectionSceneOptions) {
    super();
    this.metrics = metrics;
    this.options = options;
    this.build();
  }

  protected build(): void {
    // Empty - content built in onEnter after assets load
  }

  private async loadAssets(): Promise<void> {
    try {
      const [backgroundTexture, titleTexture] = await Promise.all([
        Assets.load('/assets/background.png'),
        Assets.load('/assets/choose_warriors.png'),
      ]);

      this.backgroundSprite = new Sprite(backgroundTexture);
      this.backgroundSprite.anchor.set(0, 0);

      this.titleSprite = new Sprite(titleTexture);
      this.titleSprite.anchor.set(0.5, 0);
    } catch (error) {
      console.error('Failed to load assets:', error);
    }
  }

  private buildContent(): void {
    this.cleanupContent();

    const { width, height } = this.metrics;
    const state = this.options.getState();
    const playerNumber = this.options.getPlayerNumber();
    const playerColor = this.options.getPlayerColor();
    const defaultName = this.options.getDefaultName();
    const defaultRating = this.options.getDefaultRating();

    // Background
    if (this.backgroundSprite) {
      this.backgroundSprite.width = width;
      this.backgroundSprite.height = height;
      this.container.addChild(this.backgroundSprite);
    }

    const containerWidth = Math.min(700, width - SPACING.LG * 2);
    const buttonAreaHeight = 50 + SPACING.LG;

    // Title image - scale proportionally based on screen size
    let titleHeight = 0;
    if (this.titleSprite) {
      // Limit title to 60% of width and 10% of height, with a max of 400px wide
      const maxTitleWidth = Math.min(width * 0.6, 400);
      const maxTitleHeight = height * 0.1;
      const scaleByWidth = maxTitleWidth / this.titleSprite.texture.width;
      const scaleByHeight = maxTitleHeight / this.titleSprite.texture.height;
      const titleScale = Math.min(scaleByWidth, scaleByHeight);
      this.titleSprite.scale.set(titleScale);
      this.titleSprite.x = width / 2;
      this.titleSprite.y = SPACING.LG;
      this.container.addChild(this.titleSprite);
      titleHeight = this.titleSprite.height;
    }

    const contentStartY = SPACING.LG + titleHeight + SPACING.MD;

    // Calculate dimensions for selection panel
    // Player info is now minimal (rating + name input = ~60px)
    const playerInfoHeight = 60;
    const warriors = state.availableWarriors;
    const cardsPerRow = 5;
    const cardGap = SPACING.SM;
    const cardWidth = Math.floor((containerWidth - (cardsPerRow - 1) * cardGap) / cardsPerRow);
    const cardHeight = cardWidth / WARRIOR_CARD_ASPECT_RATIO;
    const numRows = Math.ceil(warriors.length / cardsPerRow);
    const actualGalleryHeight = numRows * cardHeight + (numRows - 1) * cardGap + SPACING.SM;
    const maxGalleryHeight = height - contentStartY - playerInfoHeight - SPACING.SM - buttonAreaHeight - SPACING.MD;
    const galleryHeight = Math.min(actualGalleryHeight, maxGalleryHeight);

    // Create selection panel
    this.selectionPanel = new WarriorSelectionPanel({
      warriors: state.availableWarriors,
      playerColor: playerColor,
      playerName: defaultName,
      playerRating: defaultRating,
      onSelectionChange: () => this.updateUI(),
    });
    this.selectionPanel.x = (width - containerWidth) / 2;
    this.selectionPanel.y = contentStartY;
    this.selectionPanel.build(containerWidth, playerInfoHeight, galleryHeight);
    this.container.addChild(this.selectionPanel);

    // Navigation buttons - centered between bottom of cards and bottom of screen
    const buttonWidth = 120;
    const buttonGap = SPACING.MD;
    const totalButtonsWidth = buttonWidth * 3 + buttonGap * 2;
    const buttonsStartX = (width - totalButtonsWidth) / 2;

    // Calculate the bottom of the warrior cards gallery
    const cardsBottomY = contentStartY + playerInfoHeight + SPACING.SM + galleryHeight;
    // Center buttons vertically between cards bottom and screen bottom
    const buttonsY = (cardsBottomY + height) / 2;

    this.backButton = new UIButton({
      text: 'Back',
      width: buttonWidth,
      variant: 'secondary',
      onClick: () => this.handleBack(),
    });
    this.backButton.x = buttonsStartX + buttonWidth / 2;
    this.backButton.y = buttonsY;
    this.container.addChild(this.backButton);

    this.scanQRButton = new UIButton({
      text: 'Scan QR',
      width: buttonWidth,
      variant: 'secondary',
      onClick: () => this.openQRScanner(),
    });
    this.scanQRButton.x = buttonsStartX + buttonWidth * 1.5 + buttonGap;
    this.scanQRButton.y = buttonsY;
    this.container.addChild(this.scanQRButton);

    this.nextButton = new UIButton({
      text: playerNumber === 1 ? 'Next' : "Let's Go!",
      width: buttonWidth,
      variant: 'primary',
      disabled: true,
      onClick: () => this.handleNext(),
    });
    this.nextButton.x = buttonsStartX + buttonWidth * 2.5 + buttonGap * 2;
    this.nextButton.y = buttonsY;
    this.container.addChild(this.nextButton);

    this.updateUI();

    // Create DOM input after layout settles
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.selectionPanel?.createNameInput();
      });
    });
  }

  private cleanupContent(): void {
    // Cleanup selection panel
    if (this.selectionPanel) {
      this.selectionPanel.dispose();
      this.selectionPanel = null;
    }

    this.container.removeChildren();
    this.backButton = null;
    this.scanQRButton = null;
    this.nextButton = null;
  }

  private updateUI(): void {
    const isReady = this.selectionPanel?.isReady() ?? false;
    if (this.nextButton) {
      this.nextButton.setDisabled(!isReady);
    }
  }

  private handleNext(): void {
    if (!this.selectionPanel) return;

    const playerName = this.selectionPanel.getPlayerName() || this.options.getDefaultName();
    const selections = this.selectionPanel.getSelections();
    const playerRating = this.selectionPanel.getPlayerRating();

    this.cleanupContent();
    this.closeQRScanner();

    this.options.onNext(playerName, selections, playerRating);
  }

  private handleBack(): void {
    this.cleanupContent();
    this.closeQRScanner();
    this.options.onBack();
  }

  // QR Scanner methods
  private async openQRScanner(): Promise<void> {
    const playerColor = this.options.getPlayerColor();

    this.scannerOverlay = document.createElement('div');
    this.scannerOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      z-index: 2000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 20px;
    `;

    const title = document.createElement('div');
    title.textContent = 'Scan Team QR Code';
    title.style.cssText = `
      font-family: ${FONTS.PRIMARY};
      font-size: 24px;
      color: white;
    `;
    this.scannerOverlay.appendChild(title);

    this.scannerVideo = document.createElement('video');
    this.scannerVideo.autoplay = true;
    this.scannerVideo.playsInline = true;
    this.scannerVideo.style.cssText = `
      width: 300px;
      height: 300px;
      object-fit: cover;
      border: 3px solid #${playerColor.toString(16).padStart(6, '0')};
      border-radius: 8px;
    `;
    this.scannerOverlay.appendChild(this.scannerVideo);

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.cssText = `
      padding: 12px 24px;
      font-family: ${FONTS.PRIMARY};
      font-size: 16px;
      background: #ef4444;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
    `;
    cancelBtn.onclick = () => this.closeQRScanner();
    this.scannerOverlay.appendChild(cancelBtn);

    document.body.appendChild(this.scannerOverlay);

    try {
      const bitmapCanvas = document.createElement('canvas');
      bitmapCanvas.getContext('2d', { willReadFrequently: true });
      this.qrCanvas = new QRCanvas({ bitmap: bitmapCanvas });
      const camera = await frontalCamera(this.scannerVideo);
      this.activeCamera = camera;

      await new Promise<void>((resolve) => {
        const checkVideo = () => {
          if (this.scannerVideo && this.scannerVideo.videoWidth > 0 && this.scannerVideo.videoHeight > 0) {
            resolve();
          } else {
            requestAnimationFrame(checkVideo);
          }
        };
        checkVideo();
      });

      this.cancelFrameLoop = frameLoop(() => {
        if (!this.qrCanvas) return;
        const result = camera.readFrame(this.qrCanvas, true);
        if (result) {
          this.handleQRResult(result);
        }
      });
    } catch (err) {
      console.error('Failed to start camera:', err);
      this.closeQRScanner();
    }
  }

  private closeQRScanner(): void {
    if (this.cancelFrameLoop) {
      this.cancelFrameLoop();
      this.cancelFrameLoop = null;
    }
    if (this.activeCamera) {
      this.activeCamera.stop();
      this.activeCamera = null;
    }
    if (this.scannerOverlay) {
      this.scannerOverlay.remove();
      this.scannerOverlay = null;
    }
    this.scannerVideo = null;
    this.qrCanvas = null;
  }

  private handleQRResult(data: string): void {
    const token = QRService.decodeToken(data);
    if (!token) {
      console.warn('Invalid QR code data');
      this.closeQRScanner();
      return;
    }

    const state = this.options.getState();
    const availableIds = state.availableWarriors.map(w => w.id);

    if (!QRService.validateWarriorIds(token.w, availableIds)) {
      console.warn('QR code contains invalid warrior IDs');
      this.closeQRScanner();
      return;
    }

    if (this.selectionPanel) {
      this.selectionPanel.setSelections(token.w);
      this.selectionPanel.setPlayerName(token.n);
      this.selectionPanel.setPlayerRating(token.r);
    }

    SoundManager.play('qrSuccess');
    this.updateUI();
    this.closeQRScanner();
  }

  /**
   * Show waiting overlay (for online mode when waiting for opponent)
   */
  showWaiting(onCancel?: () => void): void {
    // Hide any existing overlay
    this.hideWaiting();

    // Hide name input DOM element
    this.selectionPanel?.hideNameInput();

    this.waitingOverlay = new WaitingOverlay(this.metrics, {
      title: 'Waiting for Opponent',
      message: 'Your opponent is selecting their warriors...',
      onCancel: onCancel,
    });
    this.container.addChild(this.waitingOverlay);
  }

  /**
   * Hide waiting overlay
   */
  hideWaiting(): void {
    if (this.waitingOverlay) {
      this.waitingOverlay.hide();
      this.waitingOverlay = null;
    }
  }

  async onEnter(): Promise<void> {
    super.onEnter();

    await this.loadAssets();
    this.buildContent();

    this.container.alpha = 0;
    gsap.to(this.container, { alpha: 1, duration: 0.3 });
  }

  onExit(): void {
    super.onExit();
    this.cleanupContent();
    this.closeQRScanner();
    this.hideWaiting();
  }

  onResize(metrics: LayoutMetrics): void {
    this.metrics = metrics;
    if (this.isActive) {
      // Preserve selection state before rebuild
      const savedSelections = this.selectionPanel?.getSelections() ?? [];
      const savedName = this.selectionPanel?.getPlayerName() ?? this.options.getDefaultName();
      const savedRating = this.selectionPanel?.getPlayerRating() ?? this.options.getDefaultRating();
      const hadWaitingOverlay = this.waitingOverlay !== null;

      this.buildContent();

      // Restore selection state after rebuild
      if (this.selectionPanel && savedSelections.length > 0) {
        this.selectionPanel.setSelections(savedSelections);
        this.selectionPanel.setPlayerName(savedName);
        this.selectionPanel.setPlayerRating(savedRating);
        this.updateUI();
      }

      // Re-add waiting overlay if it was showing
      if (hadWaitingOverlay && this.waitingOverlay) {
        this.waitingOverlay.onResize(metrics);
      }
    }
  }

  dispose(): void {
    this.cleanupContent();
    this.closeQRScanner();
    this.hideWaiting();
    super.dispose();
  }
}
